import { Component, type ReactNode } from 'react';
import flow from 'lodash/flow';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type Client from '@atlaskit/pubsub';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { RealtimeEventType } from '../../common/types/events.tsx';
import type { EventPayload } from '../../common/types/payloads.tsx';
import { analyticEvents, analyticsComponent } from '../../common/constants/analyticEvents.tsx';
import { SOURCE_FPS } from '../../common/constants/sources.tsx';
import { asClientOnly } from './as-client-only/index.tsx';
import { withPubSubClient } from './with-pubsub-client/index.tsx';

const LOG_LOCATION = 'jira-realtime-client-fps';

type Props = {
	appId: string;
	events: string[];
	client: Client | null;
	children: ReactNode;
	onReceive: (arg1: RealtimeEventType, arg2: EventPayload, arg3: UIAnalyticsEvent) => void;
};

// eslint-disable-next-line jira/react/no-class-components
class RealtimeClientView extends Component<Props> {
	static defaultProps = {
		children: null,
	};

	componentDidMount() {
		this.subscribeToPropEvents();
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.client !== prevProps.client || this.props.events !== prevProps.events) {
			const removedEvents = prevProps.events.filter(
				(prevEvent) => !this.props.events.includes(prevEvent),
			);

			removedEvents.forEach(this.unSubscribeToEvent);

			this.subscribeToPropEvents();
		}
	}

	componentWillUnmount() {
		this.unsubscribeToPropEvents();
	}

	onReceiveEvent = (event: unknown, payload: unknown) => {
		if (fg('jirt-convert-class-to-functional-component')) {
			log.safeInfoWithoutCustomerData(LOG_LOCATION, 'Event received', {
				source: SOURCE_FPS,
				appId: this.props?.appId,
				eventType: String(event),
			});
		}
		// @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
		this.props.onReceive(event, payload);
	};

	subscribeToEvent = (eventName: string) => {
		this.props.client?.on(eventName, this.onReceiveEvent);
	};

	unSubscribeToEvent = (eventName: string) => {
		this.props.client?.off(eventName, this.onReceiveEvent);
	};

	subscribeToPropEvents = () => {
		if (this.props.client) {
			this.props.events.forEach(this.subscribeToEvent);
		}
	};

	unsubscribeToPropEvents = () => {
		if (this.props.client) {
			this.props.events.forEach(this.unSubscribeToEvent);
		}
	};

	render() {
		return this.props.children;
	}
}

const withAnalytics = AnalyticsEventToProps(analyticsComponent, {
	onReceive: analyticEvents.EVENT_RECEIVED,
});

export const RealtimeClient = flow(
	withAnalytics,
	withPubSubClient,
	asClientOnly,
)(RealtimeClientView);
export default RealtimeClient;
